import { EnvironmentHttpCommonError } from "@amoreg/contracts";
import * as Effect from "effect/Effect";
import * as Schedule from "effect/Schedule";
import * as Schema from "effect/Schema";
import { HttpClientError } from "effect/unstable/http";

export const BOOTSTRAP_TIMEOUT_MS = 15_000;
const REQUEST_TIMEOUT_MS = 5_000;

export class PrimaryEnvironmentRequestTimeoutError extends Schema.TaggedErrorClass<PrimaryEnvironmentRequestTimeoutError>()(
  "PrimaryEnvironmentRequestTimeoutError",
  { timeoutMs: Schema.Number },
) {
  override get message() {
    return `Primary environment request timed out after ${this.timeoutMs}ms.`;
  }
}

/** Safe diagnostic data: never retain an HTTP request, cookie or credential body. */
class PrimaryEnvironmentHttpFailure extends Schema.TaggedErrorClass<PrimaryEnvironmentHttpFailure>()(
  "PrimaryEnvironmentHttpFailure",
  { kind: Schema.Literals(["network", "response", "protocol"]) },
) {
  override get message() {
    switch (this.kind) {
      case "network":
        return "network request failed";
      case "response":
        return "HTTP request failed";
      case "protocol":
        return "unexpected server response";
    }
  }
}

const RequestOperation = Schema.Literals([
  "bootstrap-authentication",
  "fetch-session-state",
  "exchange-bootstrap-credential",
  "fetch-environment-descriptor",
  "create-pairing-credential",
  "list-pairing-links",
  "revoke-pairing-link",
  "list-client-sessions",
  "revoke-client-session",
  "revoke-other-client-sessions",
]);
const SafeRequestCause = Schema.Union([
  EnvironmentHttpCommonError,
  PrimaryEnvironmentRequestTimeoutError,
  PrimaryEnvironmentHttpFailure,
]);

export class PrimaryEnvironmentRequestError extends Schema.TaggedErrorClass<PrimaryEnvironmentRequestError>()(
  "PrimaryEnvironmentRequestError",
  {
    operation: RequestOperation,
    status: Schema.NullOr(Schema.Number),
    pairingLinkId: Schema.optional(Schema.String),
    sessionId: Schema.optional(Schema.String),
    cause: SafeRequestCause,
  },
) {
  static fromCause(input: {
    readonly operation: typeof RequestOperation.Type;
    readonly cause: unknown;
    readonly pairingLinkId?: string;
    readonly sessionId?: string;
  }): PrimaryEnvironmentRequestError {
    // The contracts own status codes. Do not duplicate their mapping here.
    const contract = EnvironmentHttpCommonError.members.find((member) =>
      Schema.is(member)(input.cause),
    );
    const httpError = HttpClientError.isHttpClientError(input.cause) ? input.cause : undefined;
    const status = contract
      ? (Schema.resolveAnnotations(contract)?.httpApiStatus ?? null)
      : (httpError?.response?.status ?? null);
    const cause = Schema.is(EnvironmentHttpCommonError)(input.cause)
      ? Schema.decodeUnknownSync(EnvironmentHttpCommonError)(input.cause)
      : input.cause instanceof PrimaryEnvironmentRequestTimeoutError
        ? input.cause
        : new PrimaryEnvironmentHttpFailure({
            kind:
              httpError?.reason._tag === "TransportError"
                ? "network"
                : status === null
                  ? "protocol"
                  : "response",
          });
    return new PrimaryEnvironmentRequestError({
      operation: input.operation,
      status,
      cause,
      ...(input.pairingLinkId !== undefined ? { pairingLinkId: input.pairingLinkId } : {}),
      ...(input.sessionId !== undefined ? { sessionId: input.sessionId } : {}),
    });
  }

  override get message(): string {
    const detail = this.status !== null ? `HTTP ${this.status}` : this.cause.message;
    return `Primary environment request failed during ${this.operation} (${detail}).`;
  }
}

export const isPrimaryEnvironmentRequestError = Schema.is(PrimaryEnvironmentRequestError);

const UNAVAILABLE_STATUSES: ReadonlyArray<number> = [502, 503, 504];

/**
 * The server could not be reached at all — a timeout, a failed connection or
 * a gateway/unavailable status, the same conditions `recoverBootstrapRead`
 * retries. That is the server restarting (a deploy) or down, not a client
 * bug: the error screen retries it and Sentry does not report it as a crash
 * (AMOREG-EVE-3, AMOREG-EVE-E). Other statuses and protocol failures stay
 * reportable.
 */
export function isServerUnreachableError(error: unknown): boolean {
  if (!isPrimaryEnvironmentRequestError(error)) return false;
  if (error.status !== null) return UNAVAILABLE_STATUSES.includes(error.status);
  return (
    error.cause._tag === "PrimaryEnvironmentRequestTimeoutError" ||
    (error.cause._tag === "PrimaryEnvironmentHttpFailure" && error.cause.kind === "network")
  );
}

/** One deadline interrupts both an active request and a retry sleep. */
export const withBootstrapDeadline = <A, E, R>(effect: Effect.Effect<A, E, R>) =>
  effect.pipe(
    Effect.timeoutOrElse({
      duration: BOOTSTRAP_TIMEOUT_MS,
      orElse: () =>
        Effect.fail(new PrimaryEnvironmentRequestTimeoutError({ timeoutMs: BOOTSTRAP_TIMEOUT_MS })),
    }),
  );

const withRequestTimeout = <A, E, R>(effect: Effect.Effect<A, E, R>) =>
  effect.pipe(
    Effect.timeoutOrElse({
      duration: REQUEST_TIMEOUT_MS,
      orElse: () =>
        Effect.fail(new PrimaryEnvironmentRequestTimeoutError({ timeoutMs: REQUEST_TIMEOUT_MS })),
    }),
  );

function retryableRead(error: unknown): boolean {
  if (error instanceof PrimaryEnvironmentRequestTimeoutError) return true;
  if (!HttpClientError.isHttpClientError(error)) return false;
  if (error.reason._tag === "TransportError") return true;
  // A decode failure on a successful response is a protocol problem, not a restart.
  return error.response !== undefined && [502, 503, 504].includes(error.response.status);
}

/** Keep the HttpApi client's typed error channel intact until the public boundary. */
export const recoverBootstrapRead = <A, E, R>(effect: Effect.Effect<A, E, R>) =>
  effect.pipe(
    withRequestTimeout,
    Effect.retry({ while: retryableRead, schedule: Schedule.spaced("500 millis") }),
    withBootstrapDeadline,
  );

/** A lost POST response never proves that its one-time credential was unconsumed. */
export const bootstrapCredentialExchange = withRequestTimeout;
